import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/globals.css';

// CRITICAL: Unregister any service workers that might interfere with API requests
// This must run before any API calls are made
// Note: This runs immediately, but App.tsx will also ensure unregistration completes
if ('serviceWorker' in navigator) {
  // Prevent new service worker registrations
  navigator.serviceWorker.register = function() {
    console.warn('🚫 [Main] Blocked attempt to register service worker');
    return Promise.reject(new Error('Service worker registration blocked'));
  };

  // Clear caches first
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      console.log('🔧 [Main] Clearing', cacheNames.length, 'cache(s)');
      return Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }).then(() => {
      console.log('✅ [Main] All caches cleared');
    }).catch((error) => {
      console.warn('⚠️ [Main] Error clearing caches:', error);
    });
  }

  navigator.serviceWorker.getRegistrations().then((registrations) => {
    if (registrations.length > 0) {
      console.warn('⚠️ [Main] Found', registrations.length, 'service worker(s) - unregistering...');
      registrations.forEach((registration) => {
        registration.unregister().then((success) => {
          if (success) {
            console.log('✅ [Main] Service worker unregistered:', registration.scope);
          } else {
            console.warn('⚠️ [Main] Failed to unregister:', registration.scope);
          }
        });
      });
    }
  });
}

// Debug logging - this should appear immediately when the module loads
console.log('🚀 main.tsx module loaded');
console.log('📍 Current URL:', window.location.href);
console.log('📍 Pathname:', window.location.pathname);
console.log('📍 Search:', window.location.search);
console.log('📍 React imports:', { StrictMode: typeof StrictMode, ReactDOM: typeof ReactDOM });

// Send immediate ready message to parent (before React loads)
// This ensures the loader knows the iframe is loaded even if React fails
try {
  if (window.parent && window.parent !== window) {
    const readyMessage = {
      type: 'widget:ready',
      timestamp: new Date().toISOString(),
      messageId: `msg_${Date.now()}_init`,
      origin: window.location.origin,
      data: {
        version: '1.0.0',
        initialHeight: 600,
        source: 'main.tsx-before-react'
      }
    };
    console.log('📤 [Main] Sending immediate WIDGET_READY to parent:', readyMessage);
    // Use specific origin instead of wildcard for security
    // In production, this should be the parent origin from handshake
    // For now, use current origin (will be updated after handshake)
    try {
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : window.location.origin;
      window.parent.postMessage(readyMessage, parentOrigin);
    } catch {
      // Fallback to same origin if referrer parsing fails
      window.parent.postMessage(readyMessage, window.location.origin);
    }
  }
} catch (error) {
  console.error('❌ [Main] Failed to send immediate ready message:', error);
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('❌ Root element not found!');
} else {
  console.log('✅ Root element found:', rootElement);
  
  try {
    const root = ReactDOM.createRoot(rootElement);
    console.log('✅ React root created');
    
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
    console.log('✅ App rendered');
  } catch (error) {
    console.error('❌ Error rendering app:', error);
    // Send error message to parent
    try {
      if (window.parent && window.parent !== window) {
        // Use specific origin instead of wildcard
        try {
          const parentOrigin = document.referrer ? new URL(document.referrer).origin : window.location.origin;
          window.parent.postMessage({
          type: 'widget:error',
          timestamp: new Date().toISOString(),
          messageId: `msg_${Date.now()}_error`,
          origin: window.location.origin,
          data: {
            message: error instanceof Error ? error.message : 'Failed to render React app',
            error: String(error)
          }
        }, parentOrigin);
        } catch {
          // Fallback to same origin
          window.parent.postMessage({
            type: 'widget:error',
            timestamp: new Date().toISOString(),
            messageId: `msg_${Date.now()}_error`,
            origin: window.location.origin,
            data: {
              message: error instanceof Error ? error.message : 'Failed to render React app',
              error: String(error)
            }
          }, window.location.origin);
        }
      }
    } catch (e) {
      // Ignore
    }
  }
}
